import React, { useEffect, useState, useContext, useRef, SetStateAction } from "react"
import { Button, ButtonArrow, Heading, LoadingBox, Tag, ErrorSummary } from "govuk-react"
import { Link, useNavigate, useParams, useLocation } from "react-router-dom"
import { useMsal } from "@azure/msal-react"
import yn from "yn"

import { BackLink } from "../components/ui/GDS-components/BackLink"
import { WorkspaceContext } from "../contexts/WorkspaceContext"
import { WorkspaceOwnerType } from "../components/models/user"
import { graphConfig, loginRequest, trecoreConfig, trecoreServicesConfig } from "../components/Core/authConfig"
import { CallApiWithToken, HttpMethod, ResultType } from "../components/Core/fetch"

import "./WorkspaceDetail.css"
import { WorkspaceType } from "../components/models/workspace"
import { ApiEndpoint } from "../components/models/apiEndPoints"
import { TableHead } from "../components/ui/TableHead"
import { WorkspaceServiceType } from "../components/models/workspaceServices"
import { Table, Td } from "../components/ui/GDS-components/Table"
import { GetServiceType } from "../components/ui/GetTag"
import { CheckOps } from "../services/CheckOps"
import { useInterval } from "../components/hooks/useInterval"
import { ResourceVMType, VMPowerStates } from "../components/models/resource"
import { useAuthApiCall } from "../components/hooks/useAuthAPICall"
import { ButtonLink } from "../components/ui/ButtonLink"
import { AppRolesContext } from "../contexts/AppRolesContext"
import { RoleName } from "../components/models/roleNames"
import { ModalV2 } from "../components/ui/ModalV2"
import { Selectbox } from "../components/ui/Selectbox"
import { NotificationBox } from "../components/ui/NotificationBox"
import { error } from "../types"
import { UpdateWorkspaceServiceRequest } from "../components/models/workspace"
import { TextButton } from "../components/ui/TextButton"
import { Close } from "../components/ui/icons/close"
import { Complete } from "../components/ui/icons/complete"
import { Checkbox } from "../components/ui/Checkbox"
import { IsOnlineContext } from "../contexts/IsOnlineContext"
import { WorkspaceRoleName } from "../components/models/roleNames"
import { Lede } from "../components/ui/Lede"
import { MessageCard } from "../components/Error/MessageCard"

const LoadingSpinner = () => {
  return (
    <svg className="workspace-detail__loading-spinner" width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle opacity="0.2" cx="15" cy="15" r="12" stroke="#1D70B8" strokeWidth="6" strokeLinecap="round"/>
      <circle cx="15" cy="15" r="12" stroke="#1D70B8" strokeWidth="6" strokeLinecap="round" strokeDasharray="20 400"/>
    </svg>
  )
}

export const WorkspaceDetail = () => {
  const { instance, accounts } = useMsal();
  const appRolesCtx = useContext(AppRolesContext);
  const apiCall = useAuthApiCall();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workspace, setWorkspace] = useState<null | WorkspaceType>(null);
  const [workspaceOwners, setWorkspaceOwners] = useState<[] | WorkspaceOwnerType[]>([]);
  const [guacamole, setGuacamole] = useState<null | WorkspaceServiceType>(null);
  const [isWorkspaceOwner, setIsWorkspaceOwner] = useState(false);
  const [userRoles, setUserRoles] = useState<[] | string[]>([]);
  const params = useParams();
  const location: any = useLocation();
  const scope = location.state?.scope_id;

  console.log("params: ", params);
  console.log("appRolesCtx.roles: ", appRolesCtx.roles);
  console.log("scope: ", scope);

  useEffect(() => {
    scope !== undefined ? instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
      scopes: [`${location.state.scope_id}/${process.env.REACT_APP_TRE_CORE_API_USER_IMPERSONATION}`]
    }).then(async (response) => {
      await CallApiWithToken(
        response.accessToken,
        `${trecoreConfig.trecoreEndpoint}/${ApiEndpoint.Workspaces}/${params.id}`,
        HttpMethod.Get,
        ''
      ).then(response => {
        setWorkspace(response.workspace);
        setError(null);
        setLoading(false);
      }).catch(err => {
        setLoading(false);
        setError(err);
        console.log("error: ", err);
      })
    }) : instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
      scopes: trecoreConfig.scopes
    }).then(async (response) => {
      await CallApiWithToken(
        response.accessToken,
        `${trecoreConfig.trecoreEndpoint}/${ApiEndpoint.Workspaces}`,
        HttpMethod.Get,
        ''
      ).then(response => {
        const singleWorkspace = response.workspaces.filter((item: WorkspaceType) => item.id === params.id);
        setWorkspace(singleWorkspace[0]);
        setLoading(false);
      }).catch(err => {
        setLoading(false);
        setError(err);
        console.log("err: ", err);
      })
    })
  }, [scope]);

  const getRoles = async () => {
    await apiCall(`${ApiEndpoint.Workspaces}/${workspace?.id}`, HttpMethod.Get, workspace?.properties.scope_id, undefined, ResultType.JSON, (roles: Array<string>) => {
      roles && roles.includes(WorkspaceRoleName.WorkspaceOwner) && setIsWorkspaceOwner(true);
      roles && setUserRoles(roles);
    }, true);
  }

  useEffect(() => {
    console.log("workspace: ", workspace);
    console.log("workspace scope id: ", workspace && workspace.properties && workspace.properties.scope_id);
    (workspace && workspace.properties && workspace.properties.scope_id) && getRoles();
  }, [workspace])

  useEffect(() => {
    (workspace && workspace.properties) && (
      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      }).then(async res => {
        await CallApiWithToken(
          res.accessToken,
          graphConfig.graphMeEndpoint,
          HttpMethod.Get,
          ''
        ).then(async (response) => {
          await CallApiWithToken(
            res.accessToken,
            `${graphConfig.graphGroups}?$filter=displayName eq '${process.env.REACT_APP_ENV}-ws-${workspace.id.slice(-4)} ${graphConfig.graphGroupWorkspaceOwners}'`,
            HttpMethod.Get,
            ''
          ).then(async (response) => {
            return response.value.length > 0 ? (
              await CallApiWithToken(
                res.accessToken,
                `${graphConfig.graphGroups}/${response.value[0].id}/members`,
                HttpMethod.Get,
                ''
              ).then(response => {
                setWorkspaceOwners(response.value)
              }).catch(err => {
                console.log("err: ", err)
              })
            ) : (
              null
            )
          }).catch(err => {
            console.log("err: ", err);
          })
        }).catch((err: any) => {
          console.log("err: ", err);
        })
      })
    )
  }, [workspace]);

  console.log("userRoles: ", userRoles);

  return (
    <LoadingBox loading={loading}>
      <BackLink dynamic>Back</BackLink>
      {error && (
        <MessageCard msgData={error} />
      )}
      {(appRolesCtx.roles?.includes(RoleName.TREAdmin)) ? (
        <>
          {workspace && (
            isWorkspaceOwner ? (
              <MainPageContent
                workspace={workspace}
                guacamole={guacamole}
                setGuacamole={setGuacamole}
                workspaceOwners={workspaceOwners}
                userRoles={userRoles}
              />
            ) : (
              <Lede>You need to have the Workspace Owner role in order to view this workspace.</Lede>
            )
          )}
        </>
      ) : (
        <>
          {workspace && (
            <MainPageContent
              workspace={workspace}
              guacamole={guacamole}
              setGuacamole={setGuacamole}
              workspaceOwners={workspaceOwners}
              userRoles={userRoles}
            />
          )}
        </>
      )}
    </LoadingBox>
  )
}

type MainPageContentProps = {
  workspace: WorkspaceType;
  guacamole: WorkspaceServiceType | null;
  setGuacamole: React.Dispatch<SetStateAction<WorkspaceServiceType | null>>;
  workspaceOwners: [] | WorkspaceOwnerType[];
  userRoles: [] | string[];
}

const MainPageContent = ({ workspace, guacamole, setGuacamole, workspaceOwners, userRoles }: MainPageContentProps) => {
  return (
    <>
      <Heading>{workspace.properties && workspace.properties.display_name}</Heading>
      <section className="workspace-detail__flex-wrapper">
        <article>
          <Heading as="h2" size={36} className="workspace-detail__owners-heading">Workspace owner(s)</Heading>
          {workspaceOwners && workspaceOwners.length > 0 && (
            <ul className="workspace-detail__owners">
              {workspaceOwners.map((owner: WorkspaceOwnerType, i: number) => (
                <li key={i} className="workspace-detail__owner">{owner.displayName}</li>
              ))}
            </ul>
          )}
        </article>
        <article className="workspace-detail__airlock">
          <Heading as="h2" size={36} className="workspace-detail__blue-heading">Airlock</Heading>
          <p className="workspace-detail__airlock-copy">Use the CPRD Safe airlock for secure imports and exports.</p>
          <Button
            as={Link}
            buttonColour="#3f78c0"
            className="workspace-detail__airlock-button"
            icon={<ButtonArrow />}
            to={`/workspaces/${workspace.id}/airlocks`}
            start
          >
            Access the airlock
          </Button>
        </article>
      </section>
      {workspace.properties && <WorkspaceServices workspace={workspace} setGuacamole={setGuacamole} />}
      {(workspace.properties && guacamole) && <VirtualMachines workspace={workspace} guacamole={guacamole} userRoles={userRoles} />}
    </>
  )
}

type WorkspaceServicesProps = {
  workspace: WorkspaceType;
  setGuacamole: React.Dispatch<SetStateAction<WorkspaceServiceType | null>>;
}

const WorkspaceServices = ({ workspace, setGuacamole }: WorkspaceServicesProps) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const appRolesCtx = useContext(AppRolesContext);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<[] | WorkspaceServiceType[]>([]);
  const [refresh, setRefresh] = useState(false);
  // what are we doing with API calls that error?
  const [errorData, setErrorData] = useState(null);

  useEffect(() => {
    workspace.properties && (
      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
        scopes: [`${workspace.properties.scope_id}/${process.env.REACT_APP_TRE_CORE_API_USER_IMPERSONATION}`]
      }).then(async (response) => {
        await CallApiWithToken(
          response.accessToken,
          `${trecoreServicesConfig.trecoreEndpoint}/${ApiEndpoint.Workspaces}/${workspace.id}/${ApiEndpoint.WorkspaceServices}`,
          HttpMethod.Get,
          ''
        ).then(response => {
          setServices(response.workspaceServices);
          const guacamole = response.workspaceServices.find((service: WorkspaceServiceType) => service.templateName === "tre-service-guacamole");
          setGuacamole(guacamole ? guacamole : null);
          setLoading(false);
        }).catch((err: any) => {
          setErrorData(err);
          setLoading(false);
          console.log("error: ", err);
        })
      })
    ) 
    // do we need setUpdatedService?
    refresh && setRefresh(false);
  }, [workspace, refresh]);

  const Headers = [
    {
      header: "Name"
    },
    {
      header: "Type of service"
    },
    {
      header: "Status"
    }
  ];

  return (
    <LoadingBox loading={loading}>
      <section className="workspace-detail__section">
        <header className="workspace-detail__services-header">
          <Heading as="h2" size={36}>Workspace services</Heading>
          {(appRolesCtx.roles?.includes(RoleName.TREAdmin)) && (
            <Button
              buttonColour="#3f78c0"
              className="workspace-services__create-new"
              icon={<ButtonArrow />}
              onClick={() => navigate(`/workspaces/${workspace.id}/CreateNewWorkspaceService`)}
              start
            >
              Create new workspace service
            </Button>
          )}
        </header>
        <Table>
          <TableHead headers={Headers} className="workspace-detail__th" />
          {services && (
            <tbody>
              {services.map((service: WorkspaceServiceType) => <Service key={service.id} service={service} setRefresh={setRefresh} />)}
            </tbody>
          )}
        </Table>
      </section>
    </LoadingBox>
  )
}

type ServiceProps = {
  service: WorkspaceServiceType;
  setRefresh: React.Dispatch<SetStateAction<boolean>>
}

const Service = ({ service, setRefresh }: ServiceProps) => {
  const { instance, accounts } = useMsal();
  const workspaceCtx = useContext(WorkspaceContext);
  const workspace = workspaceCtx.workspace;
  const [updating, setUpdating] = useState(false);

  const callOperations = () => {
    instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
      scopes: [`${workspace.properties.scope_id}/${process.env.REACT_APP_TRE_CORE_API_USER_IMPERSONATION}`]
    }).then(async (response) => {
      await CallApiWithToken(
        response.accessToken,
        `${trecoreServicesConfig.trecoreEndpoint}/${ApiEndpoint.Workspaces}/${workspace.id}/${ApiEndpoint.WorkspaceServices}/${service.id}/${ApiEndpoint.Operations}`,
        HttpMethod.Get,
        ''
      ).then(response => {
        CheckOps(service, response.operations, setUpdating, setRefresh, updating);
      })
    })
  }

  useEffect(() => {
    workspace.properties && callOperations();
  }, [workspace]);

  return (
    <ServiceItem service={service} updating={updating} callOperations={callOperations} />
  )
}

type ServiceItemProps = {
  service: WorkspaceServiceType;
  updating: boolean;
  callOperations: () => void;
}

const ServiceItem = ({ service, updating, callOperations }: ServiceItemProps) => {

  useInterval(() => callOperations(), updating ? 10000 : null);

  return (
    <tr>
      <Td className="workspace-detail__td">{service.properties.display_name}</Td>
      <Td className="workspace-detail__td"><GetServiceType templateName={service.templateName} /></Td>
      <Td>{updating ? "updating..." : <Tag inactive={!service.isEnabled}>{service.isEnabled ? "active" : "inactive"}</Tag>}</Td>
    </tr>
  )
}

type VirtualMachinesProps = {
  workspace: WorkspaceType;
  guacamole: null | WorkspaceServiceType;
  userRoles: [] | string[];
}

const VirtualMachines = ({ workspace, guacamole, userRoles }: VirtualMachinesProps) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const appRolesCtx = useContext(AppRolesContext);
  const [loading, setLoading] = useState(true);
  const [allVMs, setAllVMs] = useState<[] | ResourceVMType[]>([])
  const [activeVMs, setActiveVMs] = useState<[] | ResourceVMType[]>([]);
  const [refresh, setRefresh] = useState(false);

  const isResearcherOnly = (userRoles.length === 1 && userRoles.includes(WorkspaceRoleName.WorkspaceResearcher));

  useEffect(() => {
    guacamole && (
      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
        scopes: [`${workspace.properties.scope_id}/${process.env.REACT_APP_TRE_CORE_API_USER_IMPERSONATION}`]
      }).then(async (response) => {
        await CallApiWithToken(
          response.accessToken,
          `${trecoreServicesConfig.trecoreEndpoint}/${ApiEndpoint.Workspaces}/${workspace.id}/${ApiEndpoint.WorkspaceServices}/${guacamole.id}/${ApiEndpoint.UserResources}`,
          HttpMethod.Get,
          null
        ).then(response => {
          const activeVMs = response.userResources.filter((vm: ResourceVMType) => vm.isEnabled === true);
          setAllVMs(response.userResources);
          isResearcherOnly ? setActiveVMs(activeVMs) : setActiveVMs(response.userResources);
          setLoading(false);
        })
      }).catch(err => {
        console.log("err: ", err);
        setLoading(false);
      })
    )

    refresh && setRefresh(false);
  }, [workspace, guacamole, refresh]);

  // what to do when parent workspace service = temp solution!
  // add condition for it guacamole not set and the conditions needed for different workspace 
  // user to be able to create a VM

  const Headers = [
    {
      header: "Name"
    },
    {
      header: "User"
    },
    {
      header: "Status"
    },
    {
      header: "Size",
      colSpan: 2
    }
  ];

  return (
    <>
      {guacamole?.isEnabled ? (
        <LoadingBox loading={loading}>
          <section className="workspace-detail__section">
            <Heading as="h2" size={36}>Virtual machines</Heading>
            {(appRolesCtx.roles?.includes(RoleName.TREAdmin)) && (
              <Button
                buttonColour="#3f78c0"
                onClick={() => navigate(`/workspaces/${workspace.id}/workspace-services/${guacamole.id}/createvm`)}
              >
                Create new VM
              </Button>
            )}
            {(allVMs.length < 1 && activeVMs.length < 1) && (
              <article className="workspace-deatil__no-vms">
                <Heading as="h3" size={36} className="workspace-detail__blue-heading workspace-detail__no-vms-heading">Create a new VM</Heading>
                <div className="workspace-deatil__no-vms-wrapper">
                  <p className="workspace-deatil__no-vms-copy">Create a VM to connect to CPRD Safe and access the workspace</p>
                  <Button
                    buttonColour="#3f78c0"
                    className="workspace-detail__button-no-margin"
                    icon={<ButtonArrow />}
                    onClick={() => navigate(`/workspaces/${workspace.id}/workspace-services/${guacamole.id}/createvm`)}
                    start
                  >
                    Create new VM
                  </Button>
                </div>
              </article>
            )}
            {activeVMs.length > 0 && (
              <Table>
                <TableHead headers={Headers} className="workspace-detail__th" />
                {activeVMs && (
                  <tbody>
                    {activeVMs.map((Vm: ResourceVMType) => <VM key={Vm.id} vm={Vm} setRefresh={setRefresh} guacamole={guacamole} isResearcherOnly={isResearcherOnly} />)}
                  </tbody>
                )}
              </Table>
            )}
          </section>
        </LoadingBox>
      ) : (
        null
      )}
    </>
  )
}

type VMProps = {
 vm: ResourceVMType;
 setRefresh: React.Dispatch<SetStateAction<boolean>>;
 guacamole: null | WorkspaceServiceType;
 isResearcherOnly: boolean;
}

const VM = ({vm, setRefresh, guacamole, isResearcherOnly}: VMProps) => {
  const apiCall = useAuthApiCall();
  const { instance, accounts } = useMsal();
  const workspaceCtx = useContext(WorkspaceContext);
  const workspace = workspaceCtx.workspace;
  const [updating, setUpdating] = useState(false);
  const [invokeAction, setInvokeAction] = useState(false);
  const [vmSizeUpdated, setVmSizeUpdated] = useState(false);

  const callOperations = () => {
    instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
      scopes: [`${workspace.properties.scope_id}/${process.env.REACT_APP_TRE_CORE_API_USER_IMPERSONATION}`]
    }).then(async (response) => {
      await CallApiWithToken(
        response.accessToken,
        `${trecoreServicesConfig.trecoreEndpoint}/${ApiEndpoint.Workspaces}/${workspace.id}/${ApiEndpoint.WorkspaceServices}/${guacamole?.id}/${ApiEndpoint.UserResources}/${vm.id}/${ApiEndpoint.Operations}`,
        HttpMethod.Get,
        ''
      ).then(response => {
        CheckOps(vm, response.operations, setUpdating, setRefresh, updating);
      })
    })
  }

  const executeAction = async (action: string) => {
    await apiCall(
      `${vm.resourcePath}/${ApiEndpoint.InvokeAction}?action=${action}`,
      HttpMethod.Post,
      workspaceCtx.workspaceApplicationIdURI
    ).then(response => {
      setInvokeAction(true);
    }).catch(err => {
      console.log("VM action error: ", err);
    })
  }

  const resetAction = async () => {
    setInvokeAction(false);
  }

  useEffect(() => {
    workspace.properties && callOperations();
  }, [workspace, vmSizeUpdated]);

  useEffect(() => {
    invokeAction && callOperations();
    invokeAction && resetAction();
    // is the below correct?
    // !invokeAction && resetAction();
  }, [invokeAction]);

  return (
    <VMItem 
      vm={vm}
      updating={updating}
      callOperations={callOperations}
      executeAction={executeAction}
      setUpdating={setUpdating}
      setVmSizeUpdated={setVmSizeUpdated}
      guacamole={guacamole}
      isResearcherOnly={isResearcherOnly}
    />
  )
}

type VMItemProps = {
  vm: ResourceVMType;
  updating: boolean;
  callOperations: () => void;
  executeAction: (action: string) => void;
  setUpdating: React.Dispatch<SetStateAction<boolean>>;
  setVmSizeUpdated: React.Dispatch<SetStateAction<boolean>>;
  guacamole: null | WorkspaceServiceType;
  isResearcherOnly: boolean;
}

const VMItem = ({ vm, updating, callOperations, executeAction, setUpdating, setVmSizeUpdated, guacamole, isResearcherOnly }: VMItemProps) => {
  const [action, setAction] = useState<null | string>();
  const isOnlineCtx = useContext(IsOnlineContext);
  const [disableChangeVmSizeButton, setDisableChangeVmSizeButton] = useState(false);
  const [changeVmSizeModalOpen, setChangeVmSizeModalOpen] = useState(false);
  const [startVmModalOpen, setStartVmModalOpen] = useState(false);
  const [userSelectedDoNoShowAgain, setUserSelectedDoNotShowAgain] = useState(false);
  const [vmSizeSuccessfullyUpdated, setVmSizeSuccessfullyUpdated] = useState(false);
  useInterval(() => callOperations(), updating ? 10000 : null);

  const vMUpdateSuccess = () => {
    console.log("vmUpdateSuccess running?");
    setDisableChangeVmSizeButton(false);
    setVmSizeSuccessfullyUpdated(true);
  }

  const getLocalStorage = () => {
    const saved = localStorage.getItem("vm-do-not-show-again");
    const initial = saved ? JSON.parse(saved) : false;
    return setUserSelectedDoNotShowAgain(initial === "true" ? true : false);
  }

  useEffect(() => {
    getLocalStorage();
  }, []);

  useEffect(() => {
    (action === "Changing VM size" && updating === false) && (
      vMUpdateSuccess()
    )
  }, [updating]);

  useEffect(() => {
    console.log("action");
    updating && (
      action === null && setAction("updating")
    );
  }, [updating]);

  useEffect(() => {
    vm.azureStatus.powerState === VMPowerStates.Running && setDisableChangeVmSizeButton(true);
  }, [])

  const triggerAction = (type: string) => {
    setAction(type);
    setDisableChangeVmSizeButton(type === "Stopping VM" ? false : true);
    (type === "Starting VM") ? (
      userSelectedDoNoShowAgain ? (
        executeAction(vm.azureStatus.powerState === VMPowerStates.Running ? "stop" : "start")
      ) : (
        setStartVmModalOpen(true)
      )
    ) : (
      executeAction(vm.azureStatus.powerState === VMPowerStates.Running ? "stop" : "start")
    )
  }

  const closeStartVmModal = () => {
    setStartVmModalOpen(false);
    setDisableChangeVmSizeButton(!disableChangeVmSizeButton);
  }

  const openChangeVMSizeModal = () => {
    setChangeVmSizeModalOpen(true);
  }

  console.log("vm: ", vm);
  console.log("isResearcherOnly: ", isResearcherOnly);
  console.log("action: ", action);

  return (
    <>
      {vmSizeSuccessfullyUpdated && (
        <VmSizeChangeSuccess setVmSizeSuccessfullyUpdated={setVmSizeSuccessfullyUpdated} />
      )}
      {changeVmSizeModalOpen && (
        <VmChangeSizeModal
          resource={vm}
          setModalOpen={setChangeVmSizeModalOpen}
          setUpdating={setUpdating}
          setVmSizeUpdated={setVmSizeUpdated}
          guacamole={guacamole}
          setAction={setAction}
          setDisableChangeVmSizeButton={setDisableChangeVmSizeButton}
        />
      )}
      {startVmModalOpen && (
        <StartVmModal
          resource={vm}
          setModalOpen={setStartVmModalOpen}
          closeModal={closeStartVmModal}
          onExecute={executeAction}
        />
      )}
      <tr>
        <Td className="workspace-detail__td workspace-detail__td--extra-height">{vm.properties.display_name}</Td>
        <Td className="workspace-detail__td">{vm.user.name}</Td>
        <Td className="workspace-detail__td">{vm.azureStatus.powerState === VMPowerStates.Deallocated ? <Tag backgroundColor="#B1B4B6" color="#fff">off</Tag> : <Tag>on</Tag>}</Td>
        <Td className="workspace-detail__td">{vm.properties.vm_size ? vm.properties.vm_size : "4 CPU | 32GB RAM"} {(isOnlineCtx.isOnline && !isResearcherOnly && vm.azureStatus.powerState === VMPowerStates.Deallocated && vm.isEnabled) && <TextButton onClick={() => openChangeVMSizeModal()} disabled={disableChangeVmSizeButton}>Change</TextButton>}</Td>
        <Td className="workspace-detail__td">
          <div className="workspace-detail__button-container">
            {updating ? (
              <div className="workspace-detail__loading-wrapper">
                <LoadingSpinner />
                <span>{action}</span>
              </div>
            ) : (
              <>
                {(isOnlineCtx.isOnline && vm.isEnabled) && (
                  <>
                    <Button
                      buttonColour="#3f78c0"
                      onClick={() => triggerAction(vm.azureStatus.powerState === VMPowerStates.Running ? "Stopping VM" : "Starting VM")}
                      className="workspace-detail__table-action"
                    >
                      {vm.azureStatus.powerState === VMPowerStates.Running ? "Stop" : "Start"} VM
                    </Button>
                    {vm.azureStatus.powerState === VMPowerStates.Running && (
                      <ButtonLink className="workspace-detail__button-no-margin" href={vm.properties.connection_uri ? vm.properties.connection_uri : ""} target="_blank">Connect</ButtonLink>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </Td>
      </tr>
    </>
  )
}

type VmChangeSizeModalProps = {
  resource: ResourceVMType,
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setVmSizeUpdated: React.Dispatch<React.SetStateAction<boolean>>,
  setUpdating: React.Dispatch<React.SetStateAction<boolean>>,
  guacamole: null | WorkspaceServiceType,
  setAction: React.Dispatch<React.SetStateAction<null | string | undefined>>,
  setDisableChangeVmSizeButton: React.Dispatch<React.SetStateAction<boolean>>
}

const VmChangeSizeModal = ({ resource, setModalOpen, setVmSizeUpdated, setUpdating, guacamole, setAction, setDisableChangeVmSizeButton }: VmChangeSizeModalProps) => {
  const [initialVmSize, setInitialVmSize] = useState(resource.properties.vm_size ? resource.properties.vm_size : "4 CPU | 32GB RAM");
  const [vmSize, setVmSize] = useState(resource.properties.vm_size ? resource.properties.vm_size : "4 CPU | 32GB RAM");
  const [errors, setErrors] = useState<null | error[]>(null);
  const [apiError, setApiError] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [requestSuccessful, setRequestSuccessful] = useState<null | string>(null);
  const resourceVmSizeInputRef = useRef<HTMLSelectElement>(null);
  const workspaceCtx = useContext(WorkspaceContext);
  const { instance, accounts } = useMsal();

  const makeApiCall = async (payload: any, workspace: WorkspaceType) => {
    const tokenRequest = {
      scopes: [`${workspace.properties.scope_id}/${process.env.REACT_APP_TRE_CORE_API_USER_IMPERSONATION}`],
      account: accounts[0]
    }

    setApiError(null);
    setApiLoading(true);

    instance.acquireTokenSilent(tokenRequest).then(async (response) => {
      await CallApiWithToken(
        response.accessToken,
        `${trecoreServicesConfig.trecoreEndpoint}/${ApiEndpoint.Workspaces}/${workspace.id}/${ApiEndpoint.WorkspaceServices}/${guacamole?.id}/${ApiEndpoint.UserResources}/${resource.id}`,
        HttpMethod.Patch,
        payload,
        "*"
      ).then(response => {
        setAction("Changing VM size");
        setDisableChangeVmSizeButton(true);
        setModalOpen(false);
        setVmSizeUpdated(true);
        setUpdating(true);
        setApiLoading(false);
      }).catch((err: any) => {
        setApiError(err.message);
        setApiLoading(false);
      }).catch((err: any) => {
        setApiError(err.message);
        setApiLoading(false);
      })
      yn(process.env.REACT_APP_DEBUG) && console.log("User resource update request submitted");
    })
  }

  const createPayload = () => {
    const resourceVmSize = resourceVmSizeInputRef.current?.value;
    let properties: UpdateWorkspaceServiceRequest["properties"] = {}

    properties = {
      vm_size: resourceVmSize
    };

    const newUpdateWorkspaceServiceRequest : UpdateWorkspaceServiceRequest = {
      properties: properties
    };

    resourceVmSize === initialVmSize ? (
      setRequestSuccessful("Your VM is already this size")
    ) : (
      makeApiCall(newUpdateWorkspaceServiceRequest, workspaceCtx.workspace)
    )
  }

  const onSelection = (e: string) => {
    return e === initialVmSize ? (
      setRequestSuccessful("Your VM is already this size")
    ) : (
      setVmSize(e),
      setRequestSuccessful(null)
    )
  } 

  const hasError = (formItem: string) => {
    const checkError = (obj: error) => obj.targetName === formItem;
    return errors && errors.some(checkError);
  }

  const onSubmit = () => {
    setErrors(null);
    const newErrors = [];
    const resourceVmSize = resourceVmSizeInputRef.current?.value;
    if (resourceVmSize === "Select") {
      newErrors.push({
        targetName: "vm-size",
        text: "Select a VM size. Default size is 4 CPU | 32GB RAM"
      })
    }

    return newErrors.length > 0 ? (
      setErrors(newErrors)
    ) : (
      createPayload()
    )
  }

  const getOptions = () => {
    let options = [
      {
        name: "2 CPU | 4GB RAM",
        value: "2 CPU | 4GB RAM"
      },
      {
        name: "4 CPU | 32GB RAM",
        value: "4 CPU | 32GB RAM"
      },
      {
        name: "4 CPU | 64GB RAM",
        value: "4 CPU | 64GB RAM"
      }
    ];

    return options;
  }

  return (
    <LoadingBox loading={apiLoading}>
      <ModalV2
        action="Change"
        options={
          <>
            {(apiError || requestSuccessful) && (
              <NotificationBox
                id="notification"
                error={apiError}
                text={`${requestSuccessful ? requestSuccessful : ""}. Select a different size or press cancel.`}
              />
            )}
            {errors && (
              <ErrorSummary
                errors={errors}
                heading="There is a problem submitting your request"
                onHandleErrorClick={(e: any) => document.getElementById(e)?.scrollIntoView()}
              />
            )}
            <Selectbox
              className="workspace-detail__select"
              id="vm-size"
              label="Select a VM size"
              forwardRef={resourceVmSizeInputRef}
              defaultValue={vmSize}
              error={hasError("vm-size")}
              errorText="Select a VM size. Default size is 4 CPU | 32GB RAM"
              options={getOptions()}
              onChange={(e: string) => onSelection(e)}
            />
          </>
        }
        onAction={() => onSubmit()}
        onExit={() => setModalOpen(false)}
        title={`Change the size of ${resource.properties.display_name}`}
        disableButton={requestSuccessful ? true : false}
        copy={
          <>
            <p className="workspace-detail__modal-copy">VM size and recommended use:</p>
            <ul className="workspace-detail__ul">
              <li>4GB - view only, not for analytics</li>
              <li>32GB - most commonly used for PhD studies</li>
              <li>64GB - heavy analytics</li>
            </ul>
          </>
        }
        secondaryCopy={
          <p className="workspace-detail__modal-copy workspace-detail__modal-copy--margin-top">Changing the size of your VM will change its hourly usage cost. By continuing, you confirm that you understand the <a href="https://www.cprd.com/pricing" title="Link opens in a new browser window" target="_blank" rel="noopener noreferrer">charges</a>.</p>
        }
      />
    </LoadingBox>
  )
}

type VmSizeChangeSuccessProps = {
  setVmSizeSuccessfullyUpdated: React.Dispatch<SetStateAction<boolean>>;
}

const VmSizeChangeSuccess = ({ setVmSizeSuccessfullyUpdated }: VmSizeChangeSuccessProps) => {
  return (
    <div className="workspace-detail__size-change-success">
      <Complete />
      <article className="workspace-detail__size-change-success-wrapper">
        <header className="workspace-detail__size-change-success-header">
          <Heading className="workspace-detail__size-change-success-heading" as="h4" size={19}>Vm size changed</Heading>
          <button className="workspace-detail__size-change-success-close-button" onClick={() => setVmSizeSuccessfullyUpdated(false)}>
            <Close />
          </button>
        </header>
        <p>The size of VM has been updated successfully.</p>
      </article>
    </div>
  )
}

type StartVmModalProps = {
  resource: ResourceVMType,
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  closeModal: () => void,
  onExecute: (action: string) => void
}

const StartVmModal = ({ resource, setModalOpen, closeModal, onExecute }: StartVmModalProps) => {
  const [selected, setSelected] = useState(false);

  const onSubmit = () => {
    onExecute(resource.azureStatus.powerState === VMPowerStates.Running ? "stop" : "start");
    selected && (
      localStorage.setItem("vm-do-not-show-again", JSON.stringify("true"))
    );
    setModalOpen(false);
  }

  return (
    <ModalV2
      action="Start VM"
      options={
        <>
          <Checkbox
            label="Don't show this message again."
            checked={selected}
            onChange={() => setSelected(!selected)}
          />
        </>
      }
      onAction={() => onSubmit()}
      onExit={() => closeModal()}
      title={`Do you want to proceed?`}
      disableButton={false}
      copy={
        <>
          <p className="workspace-detail__modal-copy">As soon as you start this virtual machine it will use workspace credit.</p>
          <p className="workspace-detail__modal-copy">By continuing you confirm that you <a href="https://www.cprd.com/pricing" title="Link opens in a new browser window" target="_blank" rel="noopener noreferrer">understand the charges</a>.</p>
        </>
      }
    />
  )
}
